import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { vdjColor, vdjBeatChroma, VdjPost } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Trois lampes colorées au-dessus de l'eau.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'cymatics',
  family: 'shader',
  danceability: 0.58,
  requiresTier: 0,
  energyLevel: 0.45,
  bpmMin: 50,
  bpmMax: 160,
  movement: 0.45,
  tonality: 0.80,
  enabled: true
};

// CYMATICS : l'eau d'une coupelle ronde, vue de dessus, mise en vibration par
// la musique ; des figures de résonance apparaissent, et la lumière colorée
// qui s'y reflète dessine des mandalas liquides. Universel : il glisse entre
// un registre calme et un registre intense selon lib/adapt.js.
//
// LA SURFACE : la somme de trois MODES PROPRES de la membrane circulaire
// fixée au bord, h = Σ a_i / k_i · J_m(k_mn r) · cos(m θ + φ) · (souffle) ;
// k_mn est le n-ième zéro de J_m (la membrane ne bouge pas au bord). Les
// J_m (m = 0..12) sont tabulés une fois (récurrence de Miller, stable) dans
// une texture flottante lue en interpolation MANUELLE (le Pi ne filtre pas
// les textures flottantes). Diviser par k garde des pentes comparables d'un
// mode grossier à un mode fin.
//
// LE CHOIX DES MODES : comme en vraie cymatique, plus c'est aigu, plus c'est
// fin. La brillance (part des aigus dans les bandes d'adapt) mêlée à
// l'intensité donne une complexité c : m dans [1, 3 + 9c], n dans
// [1, 2 + 4c] (graves : m ≤ 3, n ≤ 2 ; aigus : m jusqu'à 12, n jusqu'à 6),
// tirés par une graine du morceau (chaque morceau a ses figures). Un nouveau
// mode entre par fondu d'amplitude (1 temps, ou 1,5 s sans grille) pendant
// que les autres s'éteignent : une figure se dissout dans la suivante. Le
// changement vient sur les attaques ou les temps selon adapt.pulse ; à pulse
// faible, toutes les quelques secondes, au gré des bandes.
//
// LA LUMIÈRE : la surface n'est pas dessinée par sa hauteur mais par ce
// qu'elle RÉFLÉCHIT. Trois sources colorées au-dessus, à 120° les unes des
// autres, inclinées de 50° : une eau plate ne renvoie rien vers l'œil (fond
// très sombre) ; là où la pente oriente le reflet vers une source, sa couleur
// apparaît. Les bandes de réflexion ont des bords en pixels (fwidth) : des
// lignes de lumière nettes sur les crêtes (R1), une lueur douce autour.

const TAU = Math.PI * 2;
const M_MAX = 12, N_MAX = 6;
const X_MAX = 40, TABLE_N = 1024;
const DISH_R = 0.85;                  // of the half height: 85 % of the height (R6, R19)
const LIGHT_ZENITH = 50 * Math.PI / 180;
const DEEP = ['#d9a441', '#3b3ba8', '#1f6f7a'].map((h) => new THREE.Color(h));   // gold, indigo, deep teal
const WARM_WHITE = new THREE.Color(1, 0.97, 0.92);
const _vivid = [new THREE.Color(), new THREE.Color(), new THREE.Color()];
const _hsl = [{ h: 0, s: 0, l: 0 }, { h: 0, s: 0, l: 0 }, { h: 0, s: 0, l: 0 }];
const NO_BPM = 100;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- Bessel functions J_0..J_M_MAX: Miller's backward recurrence ------------------
export function besselAll(x, mMax) {
  const out = new Float64Array(mMax + 1);
  if (x < 1e-9) { out[0] = 1; return out; }
  const start = 2 * Math.ceil((Math.max(x, mMax) + 20) / 2);
  let jp1 = 0, j = 1e-30, sum = 0;
  const tmp = new Float64Array(start + 2);
  for (let k = start; k >= 1; k--) {
    const jm1 = (2 * k / x) * j - jp1;
    jp1 = j; j = jm1;
    tmp[k - 1] = j;
    if (k - 1 > 0 && (k - 1) % 2 === 0) sum += 2 * j;
    if (Math.abs(j) > 1e250) { for (let q = k - 1; q < start + 2; q++) tmp[q] *= 1e-250; j *= 1e-250; jp1 *= 1e-250; sum *= 1e-250; }
  }
  sum += tmp[0];
  for (let m = 0; m <= mMax; m++) out[m] = tmp[m] / sum;
  return out;
}
// the n-th positive zero of J_m (bisection on sign changes)
export function besselZeros(mMax, nMax) {
  const Z = [];
  for (let m = 0; m <= mMax; m++) {
    const zs = [];
    let x0 = m === 0 ? 0.5 : m + 0.5, f0 = besselAll(x0, mMax)[m];
    for (let x = x0 + 0.05; x < X_MAX + 10 && zs.length < nMax; x += 0.05) {
      const f = besselAll(x, mMax)[m];
      if (f0 * f < 0) {
        let a = x - 0.05, b = x, fa = f0;
        for (let it = 0; it < 40; it++) { const c = (a + b) / 2, fc = besselAll(c, mMax)[m]; if (fa * fc <= 0) b = c; else { a = c; fa = fc; } }
        zs.push((a + b) / 2);
      }
      f0 = f;
    }
    Z.push(zs);
  }
  return Z;
}
let TABLE = null, ZEROS = null, PEAK = null;
function besselTable() {
  if (TABLE) return TABLE;
  const data = new Float32Array(TABLE_N * (M_MAX + 2) * 4);
  PEAK = new Float64Array(M_MAX + 1);
  for (let i = 0; i < TABLE_N; i++) {
    const J = besselAll(i / (TABLE_N - 1) * X_MAX, M_MAX + 1);
    for (let m = 0; m <= M_MAX + 1; m++) data[(m * TABLE_N + i) * 4] = J[m];
    // the peak of |J'_m|: the slope a unit mode reaches
    const d = J.map((_, m) => m === 0 ? -J[1] : 0.5 * (J[m - 1] - (J[m + 1] || 0)));
    for (let m = 0; m <= M_MAX; m++) PEAK[m] = Math.max(PEAK[m], Math.abs(d[m]));
  }
  TABLE = new THREE.DataTexture(data, TABLE_N, M_MAX + 2, THREE.RGBAFormat, THREE.FloatType);
  TABLE.minFilter = TABLE.magFilter = THREE.NearestFilter;
  TABLE.needsUpdate = true;
  ZEROS = besselZeros(M_MAX, N_MAX);
  return TABLE;
}

// --- GLSL ----------------------------------------------------------------------------
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const fragmentShader = `
  precision highp float;
  uniform sampler2D uJ;
  uniform vec4 uMode[3];        // m, k (zero), amplitude / peak of |J_m|, phase phi
  uniform vec3 uLight[3];       // colours
  uniform float uLightN;
  uniform float uSlope;         // the slope the arc lights catch (in unit-disc units)
  uniform float uLevel;         // the lights' level: grows with the amplitude
  uniform float uAspect;
  uniform vec2 uStage;
  uniform float uDishR;         // screen units
  uniform float uPx;            // screen units per pixel
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform float uGain;
  varying vec2 vUv;
  // J_m(x): two texels, linear by hand (no float filtering on the Pi)
  float J(float m, float x) {
    float f = clamp(x / ${X_MAX.toFixed(1)}, 0.0, 1.0) * ${(TABLE_N - 1).toFixed(1)};
    float i = floor(f), t = f - i;
    float v = (m + 0.5) / ${(M_MAX + 2).toFixed(1)};
    float a = texture2D(uJ, vec2((i + 0.5) / ${TABLE_N.toFixed(1)}, v)).r;
    float b = texture2D(uJ, vec2((min(i + 1.0, ${(TABLE_N - 1).toFixed(1)}) + 0.5) / ${TABLE_N.toFixed(1)}, v)).r;
    return mix(a, b, t);
  }
  // the slope of h = Σ a/k J_m(k r) cos(m θ + φ), analytic:
  //   dh/dr        = a J'_m cos,           J'_m = (J_{m-1} - J_{m+1}) / 2
  //   (1/r) dh/dθ  = -a m J_m/(k r) sin,   m J_m(x)/x = (J_{m-1} + J_{m+1}) / 2   (finite at the centre)
  vec2 slope(vec2 q) {
    float r = length(q), th = atan(q.y, q.x + 1e-9);
    vec2 er = r > 1e-6 ? q / r : vec2(1.0, 0.0), et = vec2(-er.y, er.x);
    vec2 g = vec2(0.0);
    for (int i = 0; i < 3; i++) {
      vec4 md = uMode[i];
      if (md.z < 1e-4) continue;
      float x = md.y * r;
      float jl = md.x < 0.5 ? -J(1.0, x) : J(md.x - 1.0, x);
      float jh = J(md.x + 1.0, x);
      float ang = md.x * th + md.w;
      g += md.z * (0.5 * (jl - jh) * cos(ang) * er - 0.5 * (jl + jh) * sin(ang) * et);
    }
    return g;
  }
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uStage;
    vec2 q = s / uDishR;                       // the dish's unit disc
    float r = length(q);
    vec3 col = vec3(0.0);
    if (r < 1.03) {
      // three ARC LIGHTS around the dish, 120 degrees each, low over the water: the eye
      // looks straight down, so a facet sends one of them to the eye when its slope
      // reaches uSlope, and WHICH one by the direction it leans. Flat water reflects
      // the dark ceiling; the slope contours light up as thin coloured lines.
      vec2 g = slope(q);
      float gm = length(g) / uSlope;
      float fw = max(fwidth(gm), 1e-5);
      float dl = abs(gm - 1.0) / fw;             // px from the line
      float core = 1.0 - smoothstep(0.4, 1.1, dl);
      float sheath = 1.0 - smoothstep(1.2, 3.0, dl);
      float halo = exp(-dl / 5.0) * 0.22;
      float glow = smoothstep(0.35, 1.0, gm) * (1.0 - smoothstep(1.0, 1.6, gm)) * 0.10;
      float az = atan(g.y, g.x);
      vec3 lit = vec3(0.0);
      for (int i = 0; i < 3; i++) {
        if (float(i) >= uLightN) break;
        // each arc covers its 120 degrees with soft ends
        float c = cos(az - (6.2831853 * float(i) / 3.0 + 0.5));
        float w = smoothstep(0.35, 0.65, c);
        if (uLightN < 1.5) w = smoothstep(-0.2, 0.6, c);
        lit += uLight[i] * w;
      }
      lit *= uLevel;
      col = lit * (sheath * 0.62 + halo + glow) + mix(lit, vec3(1.0, 0.96, 0.9), 0.55) * core * 0.6 * step(0.01, dot(lit, vec3(1.0)));
      // the rim: a thin dark lip, the water stops at it
      float rp = (r - 1.0) * uDishR / uPx;           // px from the rim
      float inside = 1.0 - smoothstep(-0.75, 0.75, rp);
      float lip = (1.0 - smoothstep(1.0, 2.5, abs(rp - 2.0)));
      col = col * inside + vec3(0.03, 0.028, 0.03) * lip;
    }
    // the cover's mask follows the cover (R19)
    vec2 qq = abs((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uSafeCenter) - uSafeHalfSize;
    float dz = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, dz));
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;

// a seeded random from the track
function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h >>> 0; }

export class CymaticsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const fm = urlParam('cymamode');
    if (fm) { const [m, n] = fm.split(',').map((x) => parseInt(x, 10)); if (Number.isFinite(m) && Number.isFinite(n)) this._forceMode = [Math.max(0, Math.min(M_MAX, m)), Math.max(1, Math.min(N_MAX, n))]; }
    this._oneLight = urlParam('cymalight') === '1';
    const fb = parseFloat(urlParam('cymabright'));          // force the sound's brightness, 0 (low) .. 1 (high)
    this._forceBright = Number.isFinite(fb) ? Math.max(0, Math.min(1, fb)) : null;
    this._adapt = new Adapt();
    this._post = new VdjPost({ bloomStrength: 0.2, bloomThreshold: 0.80, grain: 0, vignette: 0.3, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    if (urlParam('cymadrop') === '1') this._debugDrop = true;           // a drop every 8 s
    if (urlParam('cymaprobe') === '1' && typeof window !== 'undefined') window.__cymatics = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    this._U = {
      uJ: { value: besselTable() },
      uMode: { value: [new THREE.Vector4(), new THREE.Vector4(), new THREE.Vector4()] },
      uSlope: { value: 0.5 }, uLevel: { value: 1 },
      uLight: { value: [new THREE.Color(), new THREE.Color(), new THREE.Color()] }, uLightN: { value: this._oneLight ? 1 : 3 },
      uAspect: safeZoneUniforms.uAspect, uStage: safeZoneUniforms.uStageCenter,
      uDishR: { value: DISH_R }, uPx: { value: 2 / 1080 },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather,
      uGain: { value: 1 }
    };
    this._mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._U, vertexShader: quadVertexShader, fragmentShader, depthTest: false, depthWrite: false }));
    this._mesh.frustumCulled = false; this._mesh.renderOrder = -1000;
    const self = this;
    this._mesh.onBeforeRender = function (renderer) {
      const rt = renderer.getRenderTarget();
      const h = rt ? rt.height : renderer.getDrawingBufferSize(_size).y;
      self._U.uPx.value = 2 / h;
      // 85 % of the height, or the width less 5 % margins in portrait (R6)
      self._U.uDishR.value = Math.min(DISH_R, (self._U.uAspect.value || 1) * 0.95 - 0.05);
    };
    scene.add(this._mesh);
    // the three mode slots: { m, n, k, a (current), target, phi }
    this._slots = [0, 1, 2].map(() => ({ m: 0, n: 1, k: ZEROS[0][0], a: 0, w: 0, from: 0, p: 1, target: 0, phi: 0 }));
    this._seed = 1; this._trackSeen = undefined;
    this._time = 0; this._sinceChange = 99; this._beatsSince = 0; this._onsetPrev = 0; this._phi = 0;
    this._dropT = -1; this._paletteKey = null;
    this._brill = 0.3;
    this._log = [];
    this._newMode(0.3, true);
  }

  _note(e) { e.t = +(this._time || 0).toFixed(2); this._log.push(e); if (this._log.length > 40) this._log.shift(); }
  _rnd() { this._seed = (Math.imul(this._seed, 1664525) + 1013904223) >>> 0; return this._seed / 4294967296; }

  // complexity c in [0, 1]: m in [1, 3 + 9c], n in [1, 2 + 4c]; the new mode fades in, the others out
  _newMode(c, instant) {
    let m, n;
    if (this._forceMode) [m, n] = this._forceMode;
    else {
      const mHi = Math.round(3 + 9 * c), nHi = Math.round(2 + 4 * c);
      const mLo = Math.max(0, Math.round(mHi * 0.45) - 1), nLo = Math.max(1, Math.round(nHi * 0.5));
      m = mLo + Math.floor(this._rnd() * (mHi - mLo + 1));
      n = nLo + Math.floor(this._rnd() * (nHi - nLo + 1));
    }
    m = Math.min(M_MAX, m); n = Math.min(N_MAX, Math.max(1, n));
    // the quietest slot takes it
    let slot = this._slots[0];
    for (const s of this._slots) if (s.w + s.target < slot.w + slot.target) slot = s;
    slot.m = m; slot.n = n; slot.k = ZEROS[m][n - 1]; slot.phi = m === 0 ? 0 : this._rnd() * TAU;      // m = 0: cos(phi) would scale the whole mode
    // the new one rises while the others fade: one figure dissolves into the next. Each slot
    // carries its fade's start and progress; the weight is eased (smootherstep), so the field
    // starts and ends each transition with zero speed
    for (const s of this._slots) { s.from = s.w; s.target = s === slot ? 1 : 0; s.p = 0; }
    if (instant || this._forceMode) for (const s of this._slots) { s.a = s.w = s.from = s.target; s.p = 1; }
    this._sinceChange = 0; this._beatsSince = 0;
    this._note({ what: 'mode', m, n, c: +c.toFixed(2) });
  }

  _colours(profile, a) {
    const pal = profile.artworkPalette;
    const src = (pal && (pal.vividColors || pal.dominantColors)) || [];
    const vivid = _vivid, hsl = _hsl;
    for (let k = 0; k < 3; k++) {
      if (src.length) vdjColor(src[k % src.length], vivid[k]); else vivid[k].setHSL((0.55 + k * 0.33) % 1, 0.9, 0.55);
      vivid[k].getHSL(hsl[k]);
    }
    // three hues at least 0.25 apart (R4): spread the palette's if needed
    for (let k = 1; k < 3; k++) for (let j = 0; j < k; j++) { const d = Math.abs(hsl[k].h - hsl[j].h); if (Math.min(d, 1 - d) < 0.25) { hsl[k].h = (hsl[0].h + k / 3) % 1; vivid[k].setHSL(hsl[k].h, 0.9, 0.55); } }
    // the deep register (low intensity): gold, indigo, teal; the vivid one: the palette
    const deepMix = Math.max(0, Math.min(1, (0.9 - a.saturation) / 0.25));
    const white = this._dropT >= 0 ? Math.max(0, 1 - Math.abs(this._dropT - 0.4) / 0.8) : 0;
    for (let k = 0; k < 3; k++) {
      this._U.uLight.value[k].copy(vivid[k]).lerp(DEEP[k], deepMix).lerp(WARM_WHITE, white);
    }
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const playing = profile.playbackState === 'PLAYING';
    const a = this._adapt.update(playing ? dt : 0, profile);
    if (profile.trackId !== this._trackSeen) {
      this._trackSeen = profile.trackId;
      this._seed = hashStr(String(profile.title || '') + '|' + String(profile.artist || '')) || 1;
      this._newMode(0.25, true);
    }
    this._colours(profile, a);
    if (!playing) return;                                    // frozen, never black
    this._time += dt; this._sinceChange += dt;

    // brightness of the sound: the highs' share of the bands (smoothed); without bands, the intensity
    const b = a.bands;
    const tot = b ? b.low + b.mid + b.high : 0;
    // the spectral centroid of the three bands, stretched over its usual range (0.25..0.6)
    let brill = tot > 0.05 ? Math.max(0, Math.min(1, ((b.high + 0.5 * b.mid) / tot - 0.25) / 0.35)) : 0.25 + 0.5 * a.intensity;
    if (this._forceBright != null) brill = this._forceBright;
    this._brill += (Math.min(1, brill) - this._brill) * Math.min(1, dt / 1.5);
    const c = Math.max(0, Math.min(1, 0.6 * this._brill + 0.4 * a.intensity));

    // when the figure changes: on the beats at full intensity, on attacks when pulsed, slowly otherwise
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    const beatS = 60 / bpm;
    const fade = a.pulse > 0.25 ? beatS : 1.5;
    // pulsed music: on the beats, every beat at full intensity, every 2 to 4 below;
    // free music: on an attack (rising edge of the onset envelope) after 3 s at least,
    // or a slow drift with the bands every 6 to 12 s
    let change = false;
    if (a.beat) this._beatsSince++;
    const rise = a.onset > 0.4 && this._onsetPrev <= 0.4;
    this._onsetPrev = a.onset;
    if (a.pulse > 0.3) {
      const every = a.intensity > 0.7 ? 1 : a.intensity > 0.45 ? 2 : 4;
      if (a.beat && this._beatsSince >= every) change = true;
    } else if (rise && this._sinceChange > 3) change = true;
    if (this._sinceChange > 6 + 6 * (1 - a.intensity)) change = true;
    if (change) this._newMode(c, false);
    for (const s of this._slots) {
      s.p = Math.min(1, s.p + dt / fade);
      const e = s.p * s.p * s.p * (s.p * (6 * s.p - 15) + 10);
      s.w = s.from + (s.target - s.from) * e;
      s.a = s.w;
    }
    // a slow rotation of the figure at high intensity
    this._phi += dt * 0.12 * Math.max(0, a.intensity - 0.4);
        // amplitude: low at rest, strong when intense, doubled on a drop; the standing wave breathes on the beat
    if (this._debugDrop && Math.floor(this._time / 8) !== Math.floor((this._time - dt) / 8)) this._dropT = 0;
    if (a.drop) { this._dropT = 0; this._note({ what: 'drop' }); }
    if (this._dropT >= 0) { this._dropT += dt; if (this._dropT > 2.5) this._dropT = -1; }
    const dropK = this._dropT >= 0 ? 1 + Math.max(0, 1 - this._dropT / 2.5) : 1;
    const breath = 0.9 + 0.1 * Math.cos(TAU * a.beatPhase) * (0.3 + 0.7 * a.pulse);
    this._amp = (0.55 + 0.6 * a.intensity) * dropK * breath;
    // each mode normalised by its own peak slope: a fine mode is as strong as a coarse one
    this._slots.forEach((s, i) => this._U.uMode.value[i].set(s.m, s.k, this._amp * s.a / PEAK[s.m], s.m === 0 ? 0 : s.phi + this._phi * (1 + (s.m % 3))));

    // the lights catch a fixed slope: as the amplitude grows, the contour lines move out
    // from the crests and multiply (half the peak at rest)
    // the threshold follows sqrt(A): a quiet surface keeps its lines (around its crests),
    // a strong one shows more of them, wider apart; A is the summed effective amplitude
    let A = 0; for (const s of this._slots) A += s.a;
    A *= this._amp;
    this._U.uSlope.value = 0.5 * Math.sqrt(Math.max(A, 0.05));
    this._U.uLevel.value = Math.min(1.3, 0.55 + 0.45 * A);
    // R10: the mean luminance under adapt's ceiling (the lines are thin; the glow is the mass)
    this._U.uGain.value = Math.min(1, a.lumaCap / 0.32);
    // a light RGB split at full intensity
    this._post.update(1 + (vdjBeatChroma(a.beatPhase, beatS) - 1) * Math.max(0, a.intensity - 0.6) * 0.6);
  }

  dispose() {
    if (this._scene && this._mesh) { this._mesh.onBeforeRender = null; this._scene.remove(this._mesh); }
    if (this._mesh) { this._mesh.geometry.dispose(); this._mesh.material.dispose(); }
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__cymatics === this) window.__cymatics = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
